import type { PropsWithChildren, FunctionComponent } from 'react'
import { useState } from 'react'
import { colors, useBooleanState } from '@retailer-platform/shared-components'
import { useRouteMatch } from 'react-router'
import { useCoreContext } from '../../../../utils/core/RPPCoreContext'
import { URL_APP_ADMIN } from '../../../../utils/routing/routes'
import { NavContext } from './NavContext'
import { type NavContextValue } from './nav.types'

interface NavProviderProps {}

const navStyleContext = {
  'retailer-scoped': {
    backgroundColor: colors.GRAYSCALE.X70,
    contentColor: colors.GRAYSCALE.X0,
    iconColor: 'systemGrayscale00',
  },
  admin: {
    backgroundColor: colors.GRAYSCALE.X10,
    contentColor: colors.GRAYSCALE.X50,
    iconColor: 'systemGrayscale50',
  },
  'dev-portal': {
    backgroundColor: colors.GRAYSCALE.X0,
    contentColor: colors.GRAYSCALE.X70,
    iconColor: 'systemGrayscale50',
  },
} as const

export const NavProvider: FunctionComponent<PropsWithChildren<NavProviderProps>> = ({
  children,
}) => {
  const [isNavOpen, openNav, closeNav] = useBooleanState(false)
  const [folderControlledByTour, setFolderControlledByTour] = useState('')
  const [isBlockedByModal, setIsBlockedByModal] = useState(false)
  const [isNavVisible, showNav, hideNav] = useBooleanState(true)
  const [isSideNavVisible, showSideNav, hideSideNav] = useBooleanState(true)

  const [mostRecentClickedRoute, setMostRecentClickedRoute] = useState(null)

  const adminMatch = useRouteMatch(URL_APP_ADMIN)
  const getNavType = () => {
    if (adminMatch) return 'admin-v2'
    return 'retailer-scoped-v2'
  }
  const navType = getNavType()

  const { domainNavigationEntriesV2 = {}, domainAdminNavigationEntriesV2 = {} } = useCoreContext()

  const domainEntry = () => {
    if (navType === 'admin-v2') return domainAdminNavigationEntriesV2
    if (navType === 'retailer-scoped-v2') return domainNavigationEntriesV2
  }

  const navTypeSpecificContext = {
    navigationEntries: domainEntry(),
    ...navStyleContext[navType],
  }

  const contextValue: NavContextValue = {
    isNavOpen,
    openNav,
    closeNav,
    folderControlledByTour,
    setFolderControlledByTour,
    isBlockedByModal,
    setIsBlockedByModal,
    mostRecentClickedRoute,
    setMostRecentClickedRoute,
    isNavVisible,
    showNav,
    hideNav,
    isSideNavVisible,
    showSideNav,
    hideSideNav,
    ...navTypeSpecificContext,
  }

  return <NavContext.Provider value={contextValue}>{children}</NavContext.Provider>
}
